import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  MarkerClusterer,
  InfoWindow,
} from "@react-google-maps/api";
import List from "./List";
import { zoneRadios, MarkersURL, inDevMode } from "./constants";
import { MobileInfoHeader } from "./MobileInfoHeader";

const mapLibraries = ["geometry"];

function getPosition() {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject);
  });
}

function Map() {
  const [activeMarker, setActiveMarker] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(10);
  const [map, setMap] = useState(null);
  const centerPositionRef = useRef({
    lat: 41.55913012885053,
    lng: 2.278457081462427,
  });
  const [markers, setMarkers] = useState([]);
  const [ZoneId, setZoneID] = useState("");
  const [distance, setDistance] = useState(0);
  const isDragging = useRef(false); // Nuevo ref para detectar si el usuario está arrastrando
  const zoomForEnableList = 7;
  const mapContainerStyle = {
    width: "100vw",
    height: "100vh",
    transition: "height 1s",
  };

  const mapContainerStyleWithHeader = {
    width: "100vw",
    height: "75vh",
    transition: "height 1s",
  };

  useEffect(() => {
    const fetchData = async () => {
      const markersResponse = await fetch(MarkersURL);
      const markersDataResponse = await markersResponse.json();
      markersDataResponse.forEach((marker, i) => {
        if (typeof marker.Code === "undefined") {
          marker.Code = "Marker" + i;
        }
      });
      setMarkers(markersDataResponse);
    };

    fetchData();
  }, []);

  function distanceCalculation() {
    if (!map) return;

    const point = map.getCenter();
    if (!point) return;

    const list = markers.map((marker) => {
      const distance = Math.sqrt(
        Math.pow(point.lat() - marker.Latitude, 2) +
        Math.pow(point.lng() - marker.Longitude, 2)
      );
      return { distance, ...marker };
    });

    list.sort((a, b) => a.distance - b.distance);
    setDistance(list[0]?.distance || 0);
  }

  function filtered(center) {
    if (!center) return;

    const prevCenter = centerPositionRef.current;

    if (isDragging.current) return; // Si el usuario está arrastrando, no actualizamos nada

    if (
      Math.abs(center.lat() - prevCenter.lat) > 0.05 ||
      Math.abs(center.lng() - prevCenter.lng) > 0.05
    ) {
      centerPositionRef.current = {
        lat: center.lat(),
        lng: center.lng(),
      };
    }

    if (markers.length > 0) {
      distanceCalculation();
      let zoneIdDetected = "";
      Object.keys(zoneRadios).forEach((countryCode) => {
        const {
          lat,
          lng,
          radius,
          polygon,
          debugPolygon,
          debugPolygonColor,
          usePolygon,
        } = zoneRadios[countryCode];
        const centerLatLng = new window.google.maps.LatLng(lat, lng);
        const userLatLng = new window.google.maps.LatLng(center.lat(), center.lng());
        const distanceInMeters =
          window.google.maps.geometry.spherical.computeDistanceBetween(
            userLatLng,
            centerLatLng
          );
        if (polygon && usePolygon) {
          const polygonObj = new window.google.maps.Polygon({
            paths: polygon,
            strokeColor: debugPolygonColor,
            strokeOpacity: 1,
            strokeWeight: 1,
            fillColor: debugPolygonColor,
            fillOpacity: 0.04,
          });
          if (debugPolygon) {
            polygonObj.setMap(map);
          }
          if (window.google.maps.geometry.poly.containsLocation(userLatLng, polygonObj)) {
            zoneIdDetected = countryCode;
          }
        } else {
          if (distanceInMeters <= radius) {
            zoneIdDetected = countryCode;
          }
        }
      });
      setZoneID(zoneIdDetected);
    }
  }

  const onLoad = useCallback((map) => {
    setMap(map);
    getPosition()
      .then((position) => {
        const { latitude, longitude } = position.coords;
        const center = { lat: latitude, lng: longitude };
        map.setCenter(center);
        centerPositionRef.current = center;
      })
      .catch((error) => {
        console.warn("No se pudo obtener la ubicación, usando la posición por defecto", error);
      });
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    libraries: mapLibraries,
    googleMapsApiKey: inDevMode
      ? ""
      : "AIzaSyA3XKpVG45WELAO6E9Bkpsc3UKnIgG2HfM",
  });

  return isLoaded ? (
    <>
      {currentZoom > zoomForEnableList && ZoneId.length > 0 && (
        <MobileInfoHeader key={`mobile-info-header`} ZoneId={ZoneId} />
      )}
      <GoogleMap
        mapContainerStyle={
          currentZoom > zoomForEnableList &&
            ZoneId.length > 0 &&
            window.innerWidth <= 992
            ? mapContainerStyleWithHeader
            : mapContainerStyle
        }
        center={centerPositionRef.current}
        zoom={currentZoom}
        onLoad={onLoad}
        onClick={() => setActiveMarker(null)}
        onUnmount={onUnmount}
        onZoomChanged={() => setCurrentZoom(map?.getZoom() || 10)}
        onDragStart={() => (isDragging.current = true)}
        onDragEnd={() => {
          isDragging.current = false;
          filtered(map?.getCenter());
        }}
        onBoundsChanged={() => filtered(map?.getCenter())}
      >
        <MarkerClusterer>
          {(clusterer) =>
            markers.map(({ Code,
              Name,
              Phone,
              Longitude,
              Latitude,
              Address,
              City,
              PostalCode,
              Website }) => (
              <Marker
                key={Code}
                position={{ lat: Latitude, lng: Longitude }}
                onClick={() => setActiveMarker({
                  Code,
                  Name,
                  Phone,
                  Longitude,
                  Latitude,
                  Address,
                  City,
                  PostalCode,
                  Website
                })}
                clusterer={clusterer}
              />
            ))
          }
        </MarkerClusterer>

        {activeMarker && (
          <InfoWindow
            position={{
              lat: activeMarker.Latitude,
              lng: activeMarker.Longitude,
            }}
            onCloseClick={() => setActiveMarker(null)}
          >
            <div className="infowindow">
              <h3>{activeMarker.Name}</h3>
              <p className="address-info-desktop">{activeMarker.City}</p>
              <p className="address-info-mobile">{activeMarker.Address + ", " + activeMarker.PostalCode + ", " + activeMarker.City}</p>
              <div
                className="location-phone-info"
              >
                <a
                  href={`https://www.google.com/maps/dir/Current+Location/${activeMarker.Name}, ${activeMarker.Address}, ${activeMarker.PostalCode}, ${activeMarker.City}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="/app-donde-comprar/marker.svg"
                    alt="Marker icon"
                    width="30"
                    height="30"
                  />
                </a>

                {activeMarker.Website && activeMarker.Website.length > 0 && (
                  <>
                    <div>
                      <a href={activeMarker.Website} target="_blank" rel="noopener noreferrer">
                        <img
                          src="/app-donde-comprar/globe.svg"
                          alt="Phone icon"
                          width="30"
                          height="30"
                        />
                      </a>
                    </div>
                  </>
                )}

                {typeof activeMarker.Phone === 'number' && (
                  <>
                    <div>
                      <a href={`tel:${activeMarker.Phone}`}>
                        <img
                          src="/app-donde-comprar/phone-call.svg"
                          alt="Phone icon"
                          width="30"
                          height="30"
                        />
                      </a>
                      <p>{activeMarker.Phone}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
      {currentZoom > zoomForEnableList ? (
        <List markers={markers} center={centerPositionRef.current} ZoneId={ZoneId} />
      ) : null}
    </>
  ) : null;
}

export default React.memo(Map);
